/**
 * Every user-facing string in the back office.
 *
 * The project rule: nothing hardcoded in a component. A lint rule fails on bare
 * JSX text, so the only way to put words on screen is through a key here.
 *
 * Backend error keys live here too, because the backend emits keys rather than
 * prose and this is where they become sentences.
 */
export const en = {
  // ── shell ─────────────────────────────────────────────────────────
  "app.name": "Spontar Backstage",
  "nav.dashboard": "Overview",
  "nav.people": "People",
  "nav.groups": "Groups",
  "nav.devices": "Devices",
  "nav.composer": "Composer",
  "devices.title": "Devices",
  "devices.intro": "The students' devices your institution owns. Each one is registered here once, when the box is opened. A device has no account and never signs in — it is handed to whoever is in the room, and the teacher gives it a part in the lesson.",
  "devices.enrol": "Register a device by hand",
  "devices.enrolHelp": "For a device that cannot be plugged into this computer. Everything below is printed on the device; nothing is installed here.",
  "devices.none": "No devices registered yet",
  "devices.noneBody":
    "Devices are usually added by USB from the Add devices page, once a kit exists. A registered device is admitted to any lesson in this institution.",
  "devices.column.label": "Label",
  "devices.column.id": "Device",
  "devices.column.enrolled": "Registered",
  "devices.column.state": "State",
  "devices.column.actions": "Actions",
  "devices.inService": "In service",
  "devices.notSeen": "Not seen for over {days} days",
  "devices.withdraw": "Withdraw",
  "devices.withdrawnOn": "Withdrawn {date}",
  "devices.field.label": "Label",
  "devices.field.labelHint": "Whatever is written on the case, so a teacher can match it to the thing in somebody's hand.",
  "devices.field.id": "Device number",
  "devices.field.key": "Key",
  "devices.field.keyHint": "The long code printed beside the device number.",

  // ── devices: adding over USB (K2) ────────────────────────────────
  "devices.column.kit": "Kit",
  "devices.noKit": "—",
  "devices.add.link": "Add devices",
  "devices.add.title": "Add devices",
  "devices.add.intro": "Plug a device into this computer with a USB cable, choose the kit it belongs to and press the button. The device gets the current software, its kit's Wi-Fi, and a label — you type nothing.",
  "devices.add.browser": "This page needs Chrome or Edge on a laptop or desktop computer — those are the browsers that can talk to a USB cable. Open it there and plug the device in.",
  "devices.add.back": "Back to the devices",
  "devices.add.noKits": "No kits yet",
  "devices.add.noKitsBody": "A device belongs to a kit — the box with the router it will connect to. Create the kit first.",
  "devices.add.kit": "Kit",
  "devices.add.kitHint": "The device will only find this kit's Wi-Fi. Its label starts with the kit's name.",
  "devices.add.firmware": "Software version {version} will be written to the device.",
  "devices.add.firmwareUnavailable": "The device software could not be loaded from this page. Reload and try again; if it keeps failing, tell Spontar.",
  "devices.add.retry": "Try again",
  "devices.add.resume": "Carry on without rewriting the software",
  "devices.add.startOver": "Write the software again",
  "devices.add.resumeHint": "The software is already on this device. Unplug it, plug it in again, and carry on — there is no need to write it a second time.",
  "devices.add.start": "Add the plugged-in device",
  "devices.add.steps": "Steps",
  "devices.add.step.flash": "Write the software",
  "devices.add.step.network": "Hand over the kit's Wi-Fi",
  "devices.add.step.identity": "Read the device's number",
  "devices.add.step.enrol": "Register it",
  "devices.add.state.pending": "Waiting",
  "devices.add.state.running": "Working…",
  "devices.add.state.writing": "Writing, {percent}%",
  "devices.add.state.done": "Done",
  "devices.add.state.failed": "Failed",
  "devices.add.screenDark": "This device is registered, but its screen did not come up — it will not be usable in a lesson. Plug it in and add it again with the newest software; if it still does not come up, the board is one this software does not know.",
  "devices.add.done": "{label} is ready. Unplug it, write the label on the case, and plug in the next one.",
  "devices.add.count": "{count} added to {kit} today.",
  "devices.add.error.port": "The device could not be opened. Unplug it, plug it in again and try once more.",
  "devices.add.error.portBusy": "Another program is using the device. Close it — a serial monitor, another tab — and try again.",
  "devices.add.error.noResponse": "The device did not answer. Try a different cable or USB port; some cables only charge. If it still does not answer, hold the device's reset button while pressing the button here.",
  "devices.add.error.wrongBoard": "This is not the kind of device this page can set up.",
  "devices.add.error.firmwareMissing": "The device software is not published on this page yet. Tell Spontar.",
  "devices.add.error.flash": "The software could not be written. Unplug the device, plug it in again and try once more.",
  "devices.add.error.noImprov": "The device did not start after the software was written. Unplug it, plug it in again and try once more.",
  "devices.add.error.networkNotSeen": "The device cannot hear a network called {ssid}. Switch the kit's router on next to it and give it a minute — and check the router is set to the kit's Wi-Fi name and password, on 2.4 GHz. What the device can hear: {seen}",
  "devices.add.error.networkNotSeenNothing": "The device cannot hear any Wi-Fi at all, including {ssid}. Switch the kit's router on next to it and give it a minute.",
  "devices.add.error.networkRefused": "The device can hear {ssid} but was not let on. That is almost always the password — make a new one on the Kits page and set the router to match. A network offering only 5 GHz would also do this; these devices are 2.4 GHz.",
  "devices.add.error.networkUnreachable": "The device could not reach the kit's Wi-Fi. Switch the kit's router on nearby, wait a minute, and try again.",
  "devices.add.error.network": "The device did not take the Wi-Fi. Try again; if it keeps failing, tell Spontar.",
  "devices.add.error.identity": "The device did not say who it is. Unplug it, plug it in again and try once more.",
  "devices.add.error.enrol": "The device could not be registered. Check your connection and try again.",

  // ── kits ──────────────────────────────────────────────────────────
  "nav.kits": "Kits",
  "kits.title": "Kits",
  "kits.intro": "A kit is a box for one room: a small router with its own Wi-Fi, the devices, a case. The Wi-Fi name and password are made here when you create the kit — nobody has to invent them — and shown to you for the router and the teacher's iPad. They are never sent by email.",
  "kits.new": "New kit",
  "kits.newHelp": "Give it the name that will be written on the box. Every device added to it gets a label that starts with this name — \"Set B, 07\".",
  "kits.create": "Create the kit",
  "kits.created": "{name} is ready. Its Wi-Fi name and password are below.",
  "kits.none": "No kits yet",
  "kits.noneBody":
    "Create one for each room's box. Its Wi-Fi name and password are made for you; the devices in the box are registered separately, under Devices.",
  "kits.field.name": "Name",
  "kits.field.nameHint": "Short, as it will be written on the box and on every device in it.",
  "kits.field.router": "Router",
  "kits.field.routerHint": "The teacher's app can set this router up with one tap.",
  "kits.router.gl-mt3000": "GL.iNet Beryl AX (GL-MT3000)",
  "kits.createdOn": "Created {date}",
  "kits.routerOnly": "Router: {model}. That is the one the teacher's app can set up with one tap.",
  "kits.devices": "Devices",
  "kits.devicesOwner": "The devices in this kit are registered under Devices, one at a time over USB.",
  "kits.devicesTeacher": "The devices in this kit are registered under Devices by whoever runs Spontar at your institution. There is nothing to do here.",
  "kits.network": "Wi-Fi name",
  "kits.password": "Wi-Fi password",
  "kits.showPassword": "Show",
  "kits.hidePassword": "Hide",
  "kits.copy": "Copy",
  "kits.copied": "Copied.",
  "kits.copyFailed": "Could not copy. Read it off the screen instead.",
  "kits.rename": "Rename",
  "kits.renameHelp": "Renaming the kit does not change the labels already written on its devices.",
  "kits.setNetwork": "Set the Wi-Fi",
  "kits.field.ssid": "Wi-Fi name",
  "kits.field.psk": "Wi-Fi password",
  "kits.network.help": "Leave these as they are and set the router to match. If the room already has a router you would rather not change, type what it already says here instead.",
  "kits.network.consequence": "Every device already added to this kit holds the old network and will have to be plugged in and given this one.",
  "kits.network.saved": "{name} is on its new network.",
  "kits.newPassword": "New password",
  "kits.rotate.title": "New Wi-Fi password for {name}",
  "kits.rotate.body": "The router and every device in this kit will have to be set up again with the new password before the next lesson. The Wi-Fi name stays the same. Type the kit's name to confirm.",
  "kits.rotate.confirm": "Kit name",
  "kits.rotate.mismatch": "That is not the kit's name.",
  "kits.passwordRotated": "{name} has a new Wi-Fi password. Set the router and its devices up again.",
  "kits.checklist.title": "Router settings",
  "kits.checklist.intro": "The teacher's app sets these with one tap on a router it knows. On another router, or by hand, set exactly this:",
  "kits.checklist.ssid": "Wi-Fi name:",
  "kits.checklist.psk": "Wi-Fi password:",
  "kits.checklist.band24": "2.4 GHz on, fixed channel {channels} — pick one.",
  "kits.checklist.band5": "5 GHz on, same name and password.",
  "kits.checklist.isolation": "Client isolation off — the devices must be able to see the teacher's iPad.",
  "kits.checklist.internetCheck": "Internet check off — the router must not complain that it is offline.",
  "kits.checklist.noInternet": "No internet connection. The kit works on its own.",
  "kits.firmware.title": "Device software",
  "kits.firmware.version": "Devices added from this page get version {version}.",
  "kits.firmware.unavailable": "The device software could not be loaded from this page.",
  "kits.firmware.placeholder": "No device software is published on this page yet. Devices cannot be added until it is.",
  "kits.firmware.nothingToInstall": "New devices get this version when they are added. There is nothing for you to install.",
  "kits.firmware.manifestLink": "Software manifest, for your IT department",
  "kit.error.notFound": "That kit could not be found.",
  "kit.error.nameEmpty": "A kit needs a name.",
  "kit.error.nameTooLong": "That name is too long.",
  "kit.error.ssidEmpty": "A network needs a name.",
  "kit.error.ssidTooLong": "A Wi-Fi name can be at most 32 characters.",
  "kit.error.pskTooShort": "A Wi-Fi password has to be at least 8 characters.",
  "kit.error.pskTooLong": "A Wi-Fi password can be at most 63 characters.",
  "enrolment.error.labelRequired": "A label is needed when a device is registered by hand.",
  "nav.library": "Library",
  "nav.sessions": "Lessons",
  "nav.reports": "Reports",
  "nav.institution": "Institution",
  "nav.billing": "Billing",
  "nav.legal": "Legal",
  "nav.consent": "Consent",
  "nav.signOut": "Sign out",
  "nav.menu": "Menu",
  "nav.skipToContent": "Skip to content",
  "nav.openComposer": "Open Composer",
  "nav.openComposerHint": "Where teachers write scenes. Same sign-in.",

  // ── auth ──────────────────────────────────────────────────────────
  "auth.signIn": "Sign in",
  "auth.email": "Email",
  "auth.username": "Username",
  "auth.password": "Password",
  "auth.signingIn": "Signing in…",
  "auth.identifier": "Email or username",
  "auth.identifierHint":
    "Whichever is printed on your slip. A username has no @ in it.",
  "auth.action.title": "Choose your password",
  "auth.action.checking": "Checking your link …",
  "auth.action.for": "For {email}. The same password opens Backstage and Composer.",
  "auth.action.save": "Set the password and sign in",
  "auth.action.invalid":
    "This link has run out or has already been used. A link works once, for an hour. Ask for a new one on the sign-in page under \"Forgotten your password?\", or from whoever set up your account.",
  "auth.action.done": "Your password is set. Sign in with it.",
  "auth.action.toSignIn": "Go to sign in",
  "home.teacher.groups": "Your classes",
  "home.teacher.noGroups": "You are not on a class yet. Whoever runs Spontar at your institution adds you to one.",
  "home.teacher.allGroups": "All classes",
  "home.teacher.students": "{count} students",
  "home.teacher.next": "Where the work happens",
  "home.teacher.composerBody": "Write the scenes you will teach. The same sign-in works there.",
  "home.teacher.lessons": "Lessons",
  "home.teacher.lessonsBody": "What happened in a lesson, read afterwards. The lesson itself runs on the teacher app on your tablet.",
  "home.teacher.kits": "Kits",
  "home.teacher.kitsBody": "The Wi-Fi name and password of the box in your room, for the router and your tablet.",
  "auth.sameAccount": "Same sign-in as Composer.",
  "auth.forgotPassword": "Forgotten your password?",
  "auth.forgotPasswordHelp":
    "Enter your email address above and we will send you a link.",
  "auth.sendResetLink": "Send a reset link",
  "auth.resetLinkSent":
    "If that address has an account, a link is on its way. It expires in an hour.",
  "auth.resetNeedsEmail":
    "That account signs in with a username and has no email address, so there is nowhere to send a link. Whoever administers Spontar at your institution can issue new credentials in a few seconds.",
  "auth.changePassword": "Choose your own password",
  "auth.changePasswordWhy":
    "The password you were given was printed on paper. Replace it before you do anything else.",
  "auth.newPassword": "New password",
  "auth.confirmPassword": "Repeat it",
  "password.tooShort": "At least eight characters.",
  "password.needsUppercase": "Needs one capital letter.",
  "password.needsLowercase": "Needs one small letter.",
  "password.needsNumber": "Needs one number.",
  "password.rules": "At least eight characters, with a capital, a small letter and a number.",
  "auth.passwordMismatch": "These two do not match.",
  "auth.save": "Save",

  // ── roles ─────────────────────────────────────────────────────────
  "role.owner": "Owner",
  "role.teacher": "Teacher",
  "role.student": "Student",
  "role.spontar_admin": "Spontar",

  // ── common ────────────────────────────────────────────────────────
  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.delete": "Delete",
  "common.confirm": "Confirm",
  "common.edit": "Edit",
  "common.add": "Add",
  "common.search": "Search",
  "common.loading": "Loading…",
  "common.saving": "Saving…",
  "common.saved": "Saved",
  "common.retry": "Try again",
  "common.language": "Language",
  "common.appLanguage": "App language",
  "common.none": "None",
  "common.yes": "Yes",
  "common.no": "No",
  "common.back": "Back",
  "common.required": "Required",
  "common.optional": "Optional",
  "common.opensInNewTab": "opens in a new tab",
  "toast.repeat": "×{count}",

  // ── dashboard ─────────────────────────────────────────────────────
  "dashboard.title": "Overview",
  "dashboard.welcome": "Hello, {name}.",
  "dashboard.empty": "Nothing has happened yet. Start by adding a group.",

  // ── onboarding ────────────────────────────────────────────────────
  "onboarding.title": "Getting started",
  "onboarding.intro":
    "Five things, in order. Each one links to where it is done; the list keeps itself up to date and disappears when everything is done.",
  "onboarding.progress": "{done} of {total} done",
  "onboarding.dismiss": "Hide for now",
  "onboarding.showAgain": "Show the setup steps again",
  "onboarding.step.legal": "Accept the legal documents",
  "onboarding.step.legal.hint":
    "{count} document still to accept. Lessons cannot be started until it is.",
  "onboarding.step.legal.hintPlural":
    "{count} documents still to accept. Lessons cannot be started until they are.",
  "onboarding.step.teachers": "Add your teachers",
  "onboarding.step.teachers.hint":
    "Each teacher you add by email gets a link and chooses their own password; a teacher without an address comes in through a class-list import with a printed one. You are a teacher already and do not need adding.",
  "onboarding.step.group": "Add students and put them in a group",
  "onboarding.step.group.hintNoPlayers":
    "Add students one by one, or import a class list. A student has no password — they hold a device in the lesson.",
  "onboarding.step.group.hintNoGroup":
    "A group is a class or a course. Every lesson is run with one.",
  "onboarding.step.group.hintEmptyGroup": "{name} has nobody on its roster yet. Add students to it.",
  "onboarding.step.devices": "Set up a kit and its devices",
  "onboarding.step.devices.hint":
    "A kit is the box for one room: its own Wi-Fi and the students' devices. Create the kit first; devices are added to it.",
  "onboarding.step.composer": "Open Composer and write your first scene",
  "onboarding.step.composer.hint":
    "Composer is the app where teachers write scenes. Your sign-in works there too.",
  "onboarding.teacher.title": "What you can do here",
  "onboarding.teacher.intro":
    "Backstage is where your institution is run — people, groups, devices, and what happened in past lessons. Three places matter for a teacher:",
  "onboarding.teacher.groups": "Groups",
  "onboarding.teacher.groupsBody": "The classes and courses you teach, with who is on each roster.",
  "onboarding.teacher.lessons": "Lessons",
  "onboarding.teacher.lessonsBody":
    "What happened in a lesson, reviewed afterwards. The lesson itself is run with the teacher app on your tablet.",
  "onboarding.teacher.kits": "Kits",
  "onboarding.teacher.kitsBody":
    "The Wi-Fi name and password of the box in your room, for the router and your tablet.",
  "onboarding.teacher.composerBody":
    "The scenes you teach are written in Composer, a separate app. Your username and password work there too.",
  "onboarding.teacher.dismiss": "Got it",

  // ── errors ────────────────────────────────────────────────────────
  "error.unexpected": "Something went wrong. It has been logged.",
  "error.network.unavailable":
    "We could not reach the server. Check your connection and try again.",
  "error.server.broken":
    "Something on our side is broken. It is not your connection — we have been told. Try again in a few minutes.",
  "error.auth.required": "Please sign in.",
  "error.auth.mustChangePassword": "Choose your own password before continuing.",
  "error.auth.invalidCredentials": "That username and password do not match.",
  "error.permission.denied": "You do not have access to this.",
  "error.permission.disabled": "This account has been disabled.",
  "error.permission.noInstitution": "This account is not attached to an institution.",
  "error.user.notFound": "That person could not be found.",
  "error.user.cannotDisableSelf": "You cannot disable your own account.",
  "error.user.cannotDeleteSelf": "You cannot delete your own account.",
  "error.user.cannotChangeOwnRole": "You cannot change your own role.",
  "error.user.disabled": "That account is disabled.",
  "error.institution.notFound": "Institution not found.",
  "error.group.notFound": "That group could not be found.",
  "error.group.needsATeacher": "A group needs at least one teacher.",
  "error.group.lastTeacher": "This is the group's only teacher. Add another first.",
  "error.group.notATeacher": "That person is not a teacher.",
  "error.group.notAStudent": "That person is not a student.",
  "error.group.mustTeachOwnGroup": "You can only create a group you teach.",
  "error.validation.invalidInput": "Something in this form is not valid.",
  "error.validation.displayNameEmpty": "A name is needed.",
  "error.validation.displayNameTooLong": "That name is too long.",
  "error.validation.email": "That does not look like an email address.",
  "error.validation.groupNameEmpty": "A group needs a name.",
  "error.validation.groupNameTooLong": "That name is too long.",
  "error.validation.seatsExceedTier": "That is more teachers than the {tier} tier allows.",
  "error.validation.validUntilInPast": "That date has already passed.",
  "error.validation.date": "That is not a date we can read.",
  "error.validation.rangeReversed": "The end of the range is before its start.",
  "error.validation.reasonRequired": "Please say why.",
  "error.validation.retentionOutOfRange": "Between {min} and {max} days.",
  "error.validation.retentionTooShort": "That is shorter than we can keep records for.",
  "error.validation.retentionTooLong": "That is longer than we keep records for.",
  "error.forbidden.title": "Not for you",
  "error.forbidden.bodyNamed":
    "{page} is for the institution's owner. If you should have access to it, ask whoever runs Spontar at your institution.",
  "error.forbidden.back": "Back to the overview",
  "error.forbidden.body":
    "This page is for institution owners. If you think you should have access, ask your owner.",
  "error.notFound.title": "There is nothing here",
  "error.notFound.body": "The link may be old, or the page may have moved.",

  // ── billing errors ────────────────────────────────────────────────
  "billing.error.seatLimit":
    "Your {tier} licence covers {max} teachers and {used} are in use. Upgrade to add another.",
  "billing.error.studentLimit":
    "Your licence covers {max} students and {used} are in use. Write to us for more.",
  "billing.error.licenceExpired": "The licence expired on {validUntil}.",
  "billing.error.suspended": "This institution has been suspended.",
  "billing.error.noSubscription": "There is no subscription to manage yet.",
  "billing.error.tooManyTeachersForTier":
    "That tier covers {max} teachers and {used} are in use. Remove some first.",
  "billing.error.vatIdMalformed": "That VAT ID is not in the right shape.",
  "billing.error.vatIdCountryMismatch": "That VAT ID belongs to a different country.",
  "billing.error.vatIdNotFound": "VIES does not recognise that VAT ID.",
  "billing.error.vatValidationUnavailable":
    "VIES is not responding. VAT will be charged until it can be checked; try again later.",

  // ── legal and session errors ──────────────────────────────────────
  "legal.error.documentsOutstanding": "Some documents still need to be accepted.",
  "legal.error.consentMissing": "Parental consent is missing for some students.",
  "legal.error.versionSuperseded": "A newer version has been published. Reload and read it.",
  "legal.error.documentNotFound": "That document could not be found.",
  "session.error.notFound": "That lesson could not be found.",
  "session.error.notHost": "Only the teacher running a lesson can control it.",
  "session.error.reviewIsTeacherOnly": "Only the teacher who ran the lesson can open its detail.",
  "session.error.groupArchived": "That group is archived.",
  "session.error.alreadyEnded": "That lesson has already ended.",
  "session.error.codeNotFound": "No running lesson has that code.",
  "session.error.notOnRoster": "You are not on this group's roster.",

  // ── content and import errors ─────────────────────────────────────
  "scenario.error.notFound": "That scenario could not be found.",
  "scenario.error.staleEdit": "{editedBy} saved changes while you were editing. Reload first.",
  "scenario.error.invalid": "This scenario cannot be published yet.",
  "scenario.error.versionSuperseded": "A newer version exists.",
  "import.error.fileTooLarge": "That file is too large. A class list is never this big.",
  "import.error.emptyFile": "That file is empty.",
  "import.error.unsupportedFileType": "Spontar reads CSV and XLSX files.",
  "import.error.noReadableRows": "Nothing readable was found in that file.",
  "import.error.tooManyRows": "That is more than {max} rows.",
  "import.error.nothingToImport": "There is nothing to import.",
  "import.error.ageBandRequiredForNewGroup":
    "The group {group} does not exist yet, and a new group needs an age band.",
  "import.error.noOwnerForNewGroup": "A new group needs an owner to assign it to.",
  "ai.error.budgetExceeded": "This month's AI budget has been used up.",
  "ai.error.providerUnavailable": "The writing assistant is not responding. Try again shortly.",
  "ai.error.unparseableOutput": "The assistant returned something unusable. Try again.",
  "ai.error.generatedScenarioInvalid":
    "The assistant produced a scenario that would break mid-scene. Try a different brief.",
  "ai.error.studentDataBlocked": "That request was blocked because it contained student data.",
  "enrolment.error.notFound": "That device is not registered.",
  "contact.error.email": "That does not look like an email address.",
  "contact.error.missingFields": "Please fill in the required fields.",

  // ── institution settings ──────────────────────────────────────────
  "institution.title": "Institution",
  "institution.section.identity": "Identity",
  "institution.section.defaults": "Defaults for new groups",
  "institution.section.retention": "How long records are kept",
  "institution.section.billing": "Billing details",
  "institution.name": "Name",
  "institution.country": "Country",
  "institution.countryFixed":
    "The country decides which legal documents apply, so it is not something an institution can change on its own. Write to us and we will move it.",
  "institution.segment": "Kind of institution",
  "institution.segment.studio": "Studio",
  "institution.segment.school": "School",
  "institution.locale": "Language",
  "institution.locale.en": "English",
  "institution.locale.de": "German",
  "institution.locale.fr": "French",
  "institution.localeHint":
    "The language of credential slips and of the emails we send. Each person can still choose their own.",
  "institution.defaultSessionMode": "How a lesson connects",
  "institution.mode.online": "Online",
  "institution.mode.offline": "Offline",
  "institution.modeHint":
    "Offline runs the whole lesson on the local network. A teacher can choose either when they start.",
  "institution.assessmentPreset": "Assessment framework",
  "institution.preset.ncas": "NCAS (United States)",
  "institution.preset.aoao": "AO1–AO4 (England and Wales)",
  "institution.preset.epa": "EPA / KMK (Germany)",
  "institution.preset.custom": "Your own",
  "institution.retentionDays": "Lesson records are kept for",
  "institution.retentionUnit": "days",
  "institution.retentionHint":
    "Who played which role, the scores a teacher gave, the notes they wrote. Statistics are kept indefinitely and contain nobody's name.",
  "institution.retentionShortWarning":
    "Below 90 days a teacher loses last term's record before this term ends. That is a choice you can make; it is rarely the one people mean to make.",
  "institution.billingEmail": "Where invoices go",
  "institution.vatId": "VAT identification number",
  "institution.vatIdHint": "Validated against VIES. An unvalidated number is treated as absent.",
  "institution.vat.validated": "Validated on {date}. Reverse charge applies.",
  "institution.vat.none": "No VAT number. VAT is charged at your country's rate.",
  "institution.vat.outsideScope": "Outside the scope of EU VAT.",
  "institution.vat.charge": "VAT is charged.",
  "institution.vat.revalidate": "Check again",
  "institution.vat.remove": "Remove",
  "institution.saved": "Saved.",

  // ── legal ─────────────────────────────────────────────────────────
  "legal.title": "Legal",
  "legal.docType.tos": "Terms of service",
  "legal.docType.dpa": "Data processing agreement",
  "legal.docType.avv": "Auftragsverarbeitungsvertrag",
  "legal.docType.ferpa_addendum": "FERPA addendum",
  "legal.docType.state_addendum": "State addendum",
  "legal.docType.privacy": "Privacy notice",
  "legal.docType.privacy_child": "Privacy notice for young people",
  "legal.docType.impressum": "Imprint",
  "legal.docType.parental_consent_template": "Parental consent template",
  "legal.docType.law25_addendum": "Law 25 addendum (Quebec)",
  "legal.intro":
    "What applies to you is decided by your country and the kind of institution you are, not by us and not by you.",
  "legal.column.document": "Document",
  "legal.column.version": "Version",
  "legal.column.status": "Status",
  "legal.column.published": "Published",
  "legal.column.actions": "What you can do",
  "legal.status.accepted": "Accepted by your institution",
  "legal.status.newVersion": "New version — action needed",
  "legal.status.never": "Not yet accepted",
  "legal.status.optional": "For reference",
  "legal.acceptedVersion": "You accepted version {version}",
  "legal.read": "Read",
  "legal.openOnSite": "Open the published page",
  "legal.accept": "Accept",
  "legal.acceptedToast": "Recorded.",
  "legal.scrollToEnd": "Read to the end before accepting.",
  "legal.acceptOnBehalf":
    "Accepting binds {institution}. Your name, the version and the time are recorded.",
  "legal.error.bodyUnavailable":
    "The text could not be loaded. Open the published page instead, and try again in a moment.",
  "legal.gapsTitle": "Missing from our side",
  "legal.gapsBody":
    "These documents apply to you and we have not published them yet. That is ours to fix, not yours. We will write to you when they are ready.",
  "legal.outstandingBanner":
    "{count} document needs your acceptance. Lessons cannot be started until it is accepted.",
  "legal.outstandingBannerPlural":
    "{count} documents need your acceptance. Lessons cannot be started until they are accepted.",
  "legal.outstandingAction": "Read them",
  "legal.blockingTitle": "New version to read",
  "legal.blockingBody":
    "These have changed since you accepted them. Please read and accept them before continuing.",
  "legal.blockingSignOut": "Sign out instead",
  "legal.gate.firstRunTitle": "Before you start",
  "legal.gate.firstRunBody":
    "{institution} is set up. Read and accept what your institution agrees to with Spontar — it takes a few minutes and is done once. Backstage opens as soon as everything below is accepted.",
  "legal.gate.readAndAccept": "Read and accept",
  "legal.gate.ownerOnly":
    "Only the institution's owner can accept. Teachers can sign in once this is done.",
  "legal.gate.teacherTitle": "Your institution's owner has to accept the terms first",
  "legal.gate.teacherBody":
    "Before anyone at {institution} can use Backstage, its owner has to read and accept:",
  "legal.gate.teacherNext":
    "Nothing is wrong with your account. Ask the owner to sign in once and accept; after that, you are in.",
  "legal.gate.unavailableTitle": "We could not check your institution's legal file",
  "legal.gate.unavailableBody":
    "Backstage opens once it has been checked. Try again in a moment.",

  // ── sub-processors and DPIA ───────────────────────────────────────
  "legal.subprocessors.title": "Who else processes your data",
  "legal.subprocessors.body":
    "Every company that touches data from your institution, what for, and where. The published list is authoritative.",
  "legal.subprocessors.view": "See the published list",
  "legal.dpia.title": "Data protection impact assessment",
  "legal.dpia.body":
    "A DPIA is available on request. Most institutions do not need one for a drama lesson; a public school with a data protection officer usually asks.",
  "legal.dpia.request": "Ask us for it",

  // ── parental consent ──────────────────────────────────────────────
  "consent.title": "Parental consent",
  "consent.intro":
    "Groups with anyone under 18 need consent on file before a lesson can start. You hold the relationship with the family; we never contact them.",
  "consent.noGroups": "No group needs consent yet.",
  "consent.group.recorded": "{recorded} recorded",
  "consent.group.missing": "{count} still missing",
  "consent.group.complete": "Complete",
  "consent.notRequired": "Not required for this group",
  "consent.record": "Record consent",
  "consent.revoke": "Withdraw",
  "consent.method": "How it was given",
  "consent.method.paper_form": "Signed paper form",
  "consent.method.digital_signature": "Digital signature",
  "consent.method.verbal_recorded": "Given in person and noted",
  "consent.method.school_policy": "Covered by the school's own policy",
  "consent.note": "Note",
  "consent.noteHint": "Where the form is filed, for example. Never a parent's name or address.",
  "consent.recorded": "Recorded for {name}.",
  "consent.revoked": "Withdrawn for {name}.",
  "consent.missingHeading": "Still missing",
  "consent.recordedHeading": "On file",

  // ── people ────────────────────────────────────────────────────────
  "people.title": "People",
  "people.add": "Add someone",
  "people.import": "Import a list",
  "people.search": "Search by name or username",
  "people.filter.role": "Role",
  "people.filter.group": "Group",
  "people.filter.status": "Status",
  "people.filter.all": "All",
  "people.filter.hasDevices": "Only people with a device enrolled",
  "people.column.name": "Name",
  "people.column.role": "Role",
  "people.column.username": "Username",
  "people.column.groups": "Groups",
  "people.column.devices": "Devices",
  "people.column.lastActive": "Last seen",
  "people.column.status": "Status",
  "people.status.active": "Active",
  "people.status.disabled": "Disabled",
  "people.status.mustChangePassword": "Has not set a password",
  "people.never": "Never",
  "people.empty":
    "Nobody matches",
  "people.emptyBody":
    "Change the filters, add a teacher by hand, or import a class list.",
  "people.selected": "{count} selected",
  "people.bulk.assignGroup": "Add to a group",
  "people.bulk.resetPassword": "Issue new passwords",
  "people.bulk.revokeDevices": "Revoke devices",
  "people.bulk.disable": "Disable",
  "people.bulk.delete": "Delete",
  "people.confirm.typeCount": "Type {count} to confirm",
  "people.confirm.countMismatch": "That is not the number.",
  "people.confirm.deleteTitle": "Delete {count} people",
  "people.confirm.deleteBody":
    "Their accounts, their group memberships and their lesson records are removed. Statistics are unaffected — they contain nobody's name. This cannot be undone.",
  "people.confirm.resetTitle": "Issue new passwords to {count} people",
  "people.confirm.resetBody":
    "Their current passwords stop working immediately. New ones are printed on a fresh sheet, which you will need to hand out.",
  "people.confirm.revokeTitle": "Revoke devices for {count} people",
  "people.confirm.revokeBody":
    "Each device must be enrolled again before it can join a lesson. Do this when a phone is lost or a student leaves.",
  "people.confirm.disableTitle": "Disable {count} people",
  "people.confirm.disableBody":
    "They cannot sign in. Nothing is deleted, and you can undo this from each person's page.",
  "people.reason": "Why",
  "people.reasonHint": "Recorded in the audit log. A sentence is enough.",
  "people.intro":
    "Everyone at your institution: teachers, who sign in, and students, who hold a device in a lesson and never sign in. You are listed as the owner and count as a teacher — there is no need to add yourself.",
  "people.role.ownerYou": "Owner (you)",
  "people.mark.youOwner": "(you, owner)",
  "people.mark.owner": "(owner)",

  // ── one person ────────────────────────────────────────────────────
  "person.title": "{name}",
  "person.section.identity": "Identity",
  "person.section.groups": "Groups",
  "person.section.devices": "Devices",
  "person.section.data": "Their data",
  "person.displayName": "Name",
  "person.username": "Username",
  "people.noUsername": "Students hold an enrolled device and never sign in, so they have no username.",
  "people.noUsernameMark": "—",
  "person.noUsername": "None — this student holds an enrolled device and never signs in.",
  "person.email": "Email",
  "person.emailNone": "None. Students never receive email and none is collected.",
  "person.role": "Role",
  "person.created": "Added",
  "person.lastActive": "Last seen",
  "person.noGroups": "Not in a group yet",
  "person.noDevices": "No device enrolled",
  "person.device.enrolled": "Enrolled {date}",
  "person.device.lastSeen": "Last seen {date}",
  "person.device.revoked": "Revoked {date}",
  "person.device.revoke": "Revoke",
  "person.resetPassword": "Issue a new password",
  "person.disable": "Disable",
  "person.enable": "Enable again",
  "person.export": "Download everything we hold",
  "person.delete": "Delete this person",
  "person.deleteTitle": "Delete {name}",
  "person.deleteBody":
    "Their account, their group memberships, their enrolled devices and their lesson records are removed. Statistics are unaffected — they contain nobody's name. This cannot be undone.",
  "person.deleteConfirm": "Type {name} to confirm",
  "person.deleteMismatch": "That is not the name.",
  "person.noHistory":
    "Lesson records are reached through the lesson, not through the person. They belong to the teacher who taught it.",
  "person.passwordIssued": "New password for {name}: {password}",
  "person.passwordIssuedWarning":
    "Write it down now. It is shown once and cannot be retrieved.",
  "person.noCredentials": "{name} is in the register. They hold a registered device in a lesson and never sign in.",

  // ── adding one person ─────────────────────────────────────────────
  "newPerson.title": "Add someone",
  "newPerson.role": "What are they?",
  "newPerson.role.teacher": "A teacher",
  "newPerson.role.student": "A student",
  "newPerson.name": "Their name",
  "newPerson.email": "Their email",
  "newPerson.emailHint":
    "Their sign-in link is sent there, and password resets go there. A teacher without an address can only come in through a class-list import.",
  "newPerson.noEmailForStudents":
    "A student holds a device in the lesson and never signs in, so there is nothing to send them: no password, and no email address is collected for a student anywhere in Spontar — the largest single data-protection decision in the product.",
  "newPerson.locale": "Language",
  "newPerson.create": "Add",
  "newPerson.created": "{name} added.",
  "newPerson.invited": "A sign-in link is on its way to {email}. It names both apps.",
  "newPerson.inviteNotSent":
    "{name} was added, but the email could not be sent. Write to us and we will send the link again.",

  // ── import ────────────────────────────────────────────────────────
  "import.title": "Import a list",
  "import.step.upload": "The file",
  "import.step.mapping": "The columns",
  "import.step.review": "The people",
  "import.step.credentials": "The passwords",
  "import.upload.drop": "Drop a CSV or spreadsheet here, or choose a file",
  "import.upload.choose": "Choose a file",
  "import.upload.limit": "CSV or XLSX, up to 2 MB. A class list is never larger.",
  "import.upload.sheet": "Which sheet?",
  "import.upload.reading": "Reading the file…",
  "import.upload.rowsFound": "{count} rows, {columns} columns",
  "import.upload.skippedLeading":
    "{count} rows above the header were ignored — a title banner or blank lines.",
  "import.mapping.title": "What is in each column",
  "import.mapping.fromAi":
    "Proposed by the writing assistant. Nothing is created until you have seen every row.",
  "import.mapping.fromHeuristic":
    "Matched by column name. The writing assistant was not used for this file, so check it more carefully than usual.",
  "import.mapping.confidence.high": "Confident",
  "import.mapping.confidence.medium": "Fairly confident",
  "import.mapping.confidence.low": "Not confident",
  "import.mapping.field.displayName": "Name",
  "import.mapping.field.displayNameSplit": "Name, across two columns",
  "import.mapping.field.role": "Role",
  "import.mapping.field.group": "Group",
  "import.mapping.field.ageBand": "Age or year",
  "import.mapping.none": "Not in this file",
  "import.mapping.first": "First part",
  "import.mapping.second": "Second part",
  "import.mapping.splitToggle": "The name is split across two columns",
  "import.mapping.preview": "Preview",
  "import.mapping.continue": "Look at the people",
  "import.note.headerRowGuessed": "The header row was found below some other rows.",
  "import.note.nameSplitAcrossColumns": "The name appears to be split across two columns.",
  "import.note.roleColumnAbsent": "No role column. Everyone will be a student unless you say otherwise.",
  "import.note.groupColumnAbsent": "No group column. Nobody will be put in a group.",
  "import.note.ageColumnAbsent": "No age or year column.",
  "import.note.aiUnavailableUsedHeuristic":
    "The writing assistant was unavailable, so the columns were matched by name instead.",
  "import.note.lowConfidence": "The columns were hard to read. Check every one.",
  "import.review.title": "Every person, before anything is created",
  "import.review.intro":
    "Nothing exists yet. Correct anything that is wrong, exclude anyone who should not be here, and only then create the accounts.",
  "import.review.summary": "{ready} ready · {attention} need attention",
  "import.review.unreadable": "{count} rows could not be read at all and are not shown below",
  "import.review.excludedCount": "{count} excluded",
  "import.review.column.row": "Row",
  "import.review.column.name": "Name",
  "import.review.column.role": "Role",
  "import.review.column.group": "Group",
  "import.review.column.ageBand": "Age band",
  "import.review.column.problems": "Problems",
  "import.review.exclude": "Exclude",
  "import.review.include": "Include again",
  "import.review.commit": "Create {count} accounts",
  "import.review.blocked": "Fix everything marked in red before creating any accounts.",
  "import.review.back": "Back to the columns",
  "import.problem.noName": "No name",
  "import.problem.nameTooLong": "Longer than {max} characters",
  "import.problem.groupNameTooLong": "Group name longer than {max} characters",
  "import.problem.invalidAgeBand": "Not an age band we use",
  "import.problem.ageBandRequired": "{group} does not exist yet, and a new group needs an age band",
  "import.problem.duplicateInGroup": "{count} people with this name in this group",
  "import.ageBand.u13": "Under 13",
  "import.ageBand.13-15": "13 to 15",
  "import.ageBand.16-17": "16 to 17",
  "import.ageBand.adult": "18 and over",
  "import.ageBand.mixed": "Mixed",
  "import.ageBand.none": "Not set",
  "import.credentials.title": "{count} people added",
  "import.credentials.skipped": "{count} rows were not imported",
  "import.credentials.groupsCreated.one": "{count} new group was created",
  "import.credentials.groupsCreated.other": "{count} new groups were created",
  "import.credentials.warning":
    "Passwords are shown once and cannot be retrieved. Print the sheets now. If you lose them, issue new passwords — nobody can look these up, including us.",
  "import.credentials.noneNeeded": "Nothing to print.",
  "import.credentials.noneNeededWhy": "Everybody in this import is a student. Students hold a device the institution has registered and never sign in, so there are no passwords to hand out.",
  "import.credentials.staffOnly": "{count} slips, for staff only. Students hold a registered device and have no password.",
  "import.credentials.print": "Print the credential sheets",
  "import.credentials.printing": "Making the sheets…",
  "import.credentials.opened": "The sheets opened in a new tab.",
  "import.credentials.reprint":
    "Printing again issues new passwords and invalidates the ones on the old sheet.",
  "import.credentials.done": "Finished",
  "import.skip.noName": "No name in the row",
  "import.skip.nameTooLong": "The name was too long",
  "import.skip.invalidRole": "The role was not readable",
  "import.skip.groupNameTooLong": "The group name was too long",
  "import.skip.invalidAgeBand": "The age band was not readable",

  // ── groups ────────────────────────────────────────────────────────
  "groups.title": "Groups",
  "groups.new": "New group",
  "groups.showArchived": "Show archived",
  "groups.empty": "No groups yet",
  "groups.emptyBody":
    "A group is a class or a course: its teachers and the students on its roster. Every lesson is run with one. Create the first one here, or import a class list under People.",
  "groups.column.name": "Name",
  "groups.column.ageBand": "Age band",
  "groups.column.teachers": "Teachers",
  "groups.column.students": "Students",
  "groups.column.lastSession": "Last lesson",
  "groups.column.mode": "Where the class is",
  "groups.archived": "Archived",
  "groups.noSessions": "None yet",
  "groups.studentCount": "{count}",

  "group.new.title": "New group",
  "group.name": "Name",
  "group.nameHint": "What the teacher calls it. Klasse 8b, Tuesday evening, Ensemble 2.",
  "group.ageBand": "Age band",
  "group.ageBandMinorsWarning":
    "This group contains people under 18, so parental consent has to be on file before a lesson can start. You record it; we never contact a family.",
  "group.ageBandAdults": "Nobody under 18, so no parental consent is needed.",
  "group.ageBandMinorsNoConsent":
    "This group contains people under 18, so their data is handled under the rules for young people. No parental consent is needed at this age where you are.",
  "group.defaultMode": "Where the class is",
  "group.defaultModeHint": "This decides whether the lesson needs the kit's Wi-Fi.",
  "group.mode.online": "Everyone at home",
  "group.mode.offline": "In one room (with a kit)",
  "group.teachers": "Teachers",
  "group.teachersHint":
    "Any number, including you. One of them hosts each lesson.",
  "group.roster": "Students",
  "group.rosterHint": "Optional now. You can add them later, or import a list.",
  "group.create": "Create the group",
  "group.create.needsName": "Give the group a name first.",
  "group.create.needsTeacher":
    "Choose at least one teacher — a group with no teacher cannot run a lesson.",
  "group.created": "{name} created.",

  "group.tab.roster": "Roster",
  "group.tab.teachers": "Teachers",
  "group.tab.settings": "Settings",
  "group.tab.devices": "Devices",
  "group.tab.history": "Lessons",

  "group.roster.add": "Add students",
  "group.roster.remove": "Remove",
  "group.roster.removeTitle": "Remove {name} from {group}",
  "group.roster.removeBody":
    "Their devices for this group are revoked, so they cannot join a lesson with it until they enrol again. Their account and everything else stay as they are.",
  "group.roster.removeConfirm": "Type {name} to confirm",
  "group.roster.removed": "{name} removed.",
  "group.roster.empty": "Nobody on the roster yet",
  "group.roster.addedOn": "Added {date}",
  "group.roster.added": "{count} added.",
  "group.roster.chooseStudents": "Which students?",
  "group.roster.noneToAdd": "Everybody is already on this roster.",

  "group.teachers.add": "Add a teacher",
  "group.teachers.remove": "Remove",
  "group.teachers.lastOne":
    "This is the group's only teacher. A group with no teacher cannot run a lesson, so add another first.",
  "group.teachers.chooseTeacher": "Which teacher?",
  "group.teachers.noneToAdd": "Every teacher is already on this group.",

  "group.settings.archive": "Archive this group",
  "group.settings.unarchive": "Take it out of the archive",
  "group.settings.archiveBody":
    "It disappears from the list and from the app. Nothing is deleted, and you can bring it back at any time.",
  "group.settings.assessmentOverride": "Assessment framework for this group",
  "group.settings.assessmentInherit": "The same as the institution",
  "group.settings.assessmentHint":
    "The scale you already mark against. Leave it as the institution's unless this class is assessed differently.",

  "group.devices.empty": "No device is enrolled for anyone on this roster",
  "group.devices.stale": "Not used for {days} days",
  "group.devices.column.student": "Student",
  "group.devices.column.label": "Device",
  "group.devices.column.enrolled": "Enrolled",
  "group.devices.column.lastSeen": "Last seen",

  "group.history.empty": "No lesson has been run with this group yet",
  "group.history.column.date": "Date",
  "group.history.column.teacher": "Teacher",
  "group.history.column.mode": "Connected",
  "group.history.column.attendance": "Present",
  "group.history.live": "Running now",

  "group.consent.title": "Parental consent",
  "group.consent.coverage": "{recorded} of {total} recorded",
  "group.consent.complete": "Everyone on this roster has consent on file.",
  "group.consent.missing": "Missing for {names}",
  "group.consent.record": "Record consent",
  "group.consent.blocks": "A lesson cannot be started until every one of them is on file.",

  // ── learning goals ────────────────────────────────────────────────
  "goal.status.title": "Status",
  "goal.status.description":
    "Who is above whom, and what happens when that changes. The spine of most scenes that work.",
  "goal.accepting_offers.title": "Accepting offers",
  "goal.accepting_offers.description":
    "Taking what your partner gives you and building on it, instead of blocking it.",
  "goal.emotional_range.title": "Emotional range",
  "goal.emotional_range.description":
    "Playing further from your own register than is comfortable, and staying truthful there.",
  "goal.physicality.title": "Physicality",
  "goal.physicality.description":
    "The body first. What a person does before, and instead of, saying anything.",
  "goal.listening.title": "Listening",
  "goal.listening.description":
    "Being changed by what you hear. The point of concentration that makes the rest possible.",
  "goal.text_work.title": "Text work",
  "goal.text_work.description":
    "Working from a script: timing, listening for the cue, and the intention under the words.",
  "goal.source": "After {name}",

  // ── scene validation (backend keys) ─────────────────────────────
  "scenario.error.roleCount": "A scene needs between {min} and {max} roles.",
  "scenario.error.duplicateRoleId": "Two roles share an identifier.",
  "scenario.error.roleUnused": "{role} has nothing to do.",
  "scenario.error.roleNameEmpty": "A role needs a name.",
  "scenario.error.tooFewBeats": "A scene needs at least {min} beats.",
  "scenario.error.cueOrder": "The beats are out of order.",
  "scenario.error.cueUnknownRole": "This beat belongs to a role that is not in the scene.",
  "scenario.error.emotionEmpty": "A beat needs a feeling.",
  "scenario.error.duplicateCueId": "Two beats share an identifier.",
  "scenario.error.openingLength": "The opening must be between 1 and {max} characters.",
  "scenario.error.castRange": "The cast range does not fit the roles in the scene.",
  "scenario.error.unknownSource": "That is not a practice we cite.",
  "scenario.error.constraintLabelEmpty": "The repeating rule needs a name.",
  "scenario.error.consecutiveSameRole":
    "Two beats in a row for the same person. Merge them.",
  "scenario.error.situationTooLong": "A situation is at most {max} characters.",
  "scenario.error.noCues": "A scene needs at least one beat.",
  "scenario.error.tooFewRoles": "A scene needs at least two roles.",
  "scenario.error.roleWithoutCues": "{role} has nothing to do.",
  "scenario.error.cueWithoutRole": "A beat points at a role that is not in the scene.",
  "scenario.error.castRangeInvalid": "The smallest cast cannot be larger than the largest.",
  "scenario.error.openingEmpty": "There is nothing for the teacher to read aloud.",
  "scenario.error.openingTooLong": "The opening is at most {max} characters.",
  "scenario.error.titleEmpty": "A scene needs a title.",
  "scenario.error.iconUnknown": "The card cannot show this icon. Choose one from the list.",
  "scenario.error.lineEmpty": "A line needs its words.",
  "scenario.error.lineTooLong": "A line is at most 400 characters. Split it into two beats.",
  "scenario.error.imageMissing": "This beat has no picture yet.",
  "scenario.error.captionTooLong": "A caption is at most 80 characters.",
  "scenario.error.imageOnTextBeat": "Only a picture beat can carry a picture.",

  // ── sessions ──────────────────────────────────────────────────────
  "sessions.title": "Lessons",
  "sessions.empty": "No lesson has been run yet",
  "sessions.emptyBody":
    "Lessons are run with the teacher app on a tablet, not here. What happened in one is reviewed here afterwards.",
  "sessions.noMatch": "No lesson matches these filters",
  "sessions.noMatchBody": "Try \"All\" for the group, or \"All\" for how it connected.",
  "sessions.clearFilters": "Show all lessons",
  "sessions.column.date": "Date",
  "sessions.column.group": "Group",
  "sessions.column.teacher": "Teacher",
  "sessions.column.runs": "Scenes",
  "sessions.column.attendance": "Present",
  "sessions.column.duration": "Length",
  "sessions.column.mode": "Connected",
  "sessions.filter.group": "Group",
  "sessions.filter.teacher": "Teacher",
  "sessions.filter.mode": "Connected",
  "sessions.minutes": "{count} min",
  "sessions.attendance": "{present} of {total}",
  "sessions.live": "Running now",
  "sessions.ownerNotice":
    "You can see that a lesson happened, and its statistics. What happened inside it — who played which role, what the teacher wrote down — belongs to the teacher who taught it.",

  "review.title": "{group}, {date}",
  "review.tab.overview": "Overview",
  "review.tab.runs": "Scenes",
  "review.tab.scores": "Scores",
  "review.tab.notes": "Notes",
  "review.register": "Who was there",
  "review.present": "Present",
  "review.absent": "Absent",
  "review.run": "Scene {number}",
  "review.runScenario": "{title}, version {version}",
  "review.runDuration": "{count} min",
  "review.runAborted": "Stopped early",
  "review.cast": "Who played what",
  "review.castAs": "as {role}",
  "review.difficulty": "Difficulty {level}",
  "review.timeline": "Beat by beat",
  "review.liveScene": "Live scene",
  "review.beat.line": "“{text}”",
  "review.beat.picture": "Picture",
  "review.beat.pictureMissing": "Picture not available",
  "review.noRuns": "No scene was played in this lesson",

  "review.scores.intro":
    "Optional, and it stays optional. A lesson with no scores is an ordinary lesson.",
  "review.scores.framework": "Marked against {name}",
  "review.scores.none": "Not scored",
  "review.scores.clear": "Remove",
  "review.scores.saved": "Recorded.",

  "review.notes.label": "What you want to remember",
  "review.notes.retention":
    "Yours alone, and kept until {date}. Then it is deleted, along with everything else about how this lesson ran. Statistics stay.",
  "review.notes.saving": "Saving…",
  "review.notes.saved": "Saved",
  "review.retentionSoon":
    "The detail of this lesson will be deleted on {date}. Statistics are kept.",
  "review.retentionGone":
    "The detail of this lesson has been deleted. What remains is its statistics.",
  "review.teacherOnly": "Only the teacher who ran the lesson can open its detail.",
  "review.notFound": "That lesson is not here",
  "review.notFoundBody":
    "The link may be old, or the lesson may have passed out of the retention window.",
  "review.allLessons": "All lessons",

  // ── the room's record (iteration 2) ───────────────────────────────
  "sessions.kind.room": "Room lesson",
  "sessions.kind.show": "Show night",
  "review.lesson": "Lesson",
  "review.tab.room": "Room",
  "review.tab.show": "Show",
  "review.dries.title": "Dry map",
  "review.dries.intro":
    "Where each actor needed a line: prompted by you, or asked for from the card. A beat counts once, however often it was prompted. Information, never a score.",
  "review.dries.summary": "{count} dries · {prompted} prompted · {asked} asked",
  "review.dries.none": "No dries",
  "review.dries.beat": "Beat {number}",
  "review.dries.offScript": "A beat no longer in the scene",
  "review.dries.prompted": "Prompted: {text}",
  "review.dries.promptedLine": "Prompted",
  "review.dries.asked": "Asked for the line",
  "review.dries.marks": "Marks per beat",
  "review.dries.mark.clean": "Beat {number}, {role}: no dry",
  "review.dries.mark.prompted": "Beat {number}, {role}: dried, prompted",
  "review.dries.mark.asked": "Beat {number}, {role}: dried, asked for the line",
  "review.dries.mark.both": "Beat {number}, {role}: dried, asked for the line and prompted",
  "review.dries.legend": "● prompted · ○ asked",
  "review.rounds.title": "Rounds",
  "review.rounds.intro": "Each round the room played: the exercise, who was up and for how long.",
  "review.rounds.round": "Round {number}",
  "review.rounds.column.exercise": "Exercise",
  "review.rounds.column.who": "Who was up",
  "review.rounds.column.length": "Length",
  "review.rounds.untitled": "Untitled round",
  "review.rounds.empty": "No round was played in this lesson",
  "review.matrix.title": "Who worked with whom",
  "review.matrix.intro":
    "How often each pair was up together, and how often each student was up at all.",
  "review.matrix.timesUp": "Times up",
  "review.matrix.cell": "{a} with {b}: {count}",
  "review.acts.title": "Acts",
  "review.acts.intro": "The running order as it was played.",
  "review.acts.act": "Act {number}",
  "review.acts.column.title": "Act",
  "review.acts.column.performers": "On stage",
  "review.acts.column.length": "Length",
  "review.acts.untitled": "Untitled act",
  "review.acts.empty": "No act was played in this lesson",
  "review.jam.title": "Jam draws",
  "review.jam.draw": "Draw {number}",
  "review.jam.group": "Group {number}",
  "review.jam.empty": "No names were drawn",
  "group.history.dries.title": "Off-book progress",
  "group.history.dries.intro":
    "Dries per scripted scene, oldest lesson first — only lessons you taught. Information, never a score.",
  "group.history.dries.entry": "{date}: {count} dries in {beats} beats",

  // ── assessment frameworks ─────────────────────────────────────────
  "assessment.epa.name": "EPA / KMK",
  "assessment.epa.level1": "Reproduktion — repeats what was shown",
  "assessment.epa.level2": "Reorganisation — puts known things together in a new way",
  "assessment.epa.level3": "Transfer — applies it to a situation they have not seen",
  "assessment.epa.level4": "Reflexion — judges their own choices and can say why",
  "assessment.aoao.name": "AO1–AO4",
  "assessment.aoao.level1": "AO1 — creates and develops ideas",
  "assessment.aoao.level2": "AO2 — applies theatrical skills",
  "assessment.aoao.level3": "AO3 — demonstrates knowledge of practice",
  "assessment.aoao.level4": "AO4 — analyses and evaluates their own work",
  "assessment.ncas.name": "NCAS",
  "assessment.ncas.level1": "Creating — imagines and generates",
  "assessment.ncas.level2": "Performing — realises artistic work",
  "assessment.ncas.level3": "Responding — perceives and interprets",
  "assessment.ncas.level4": "Connecting — relates it to context and experience",
  "assessment.custom.name": "Your own",
  "assessment.custom.level1": "1",
  "assessment.custom.level2": "2",
  "assessment.custom.level3": "3",
  "assessment.custom.level4": "4",

  // ── reports ───────────────────────────────────────────────────────
  "reports.title": "Reports",
  "reports.noStudentData":
    "Built from statistics only. Nothing here identifies a student, and nothing here can.",
  "reports.range.month": "This month",
  "reports.range.term": "This term",
  "reports.range.year": "This year",
  "reports.range.custom": "Choose dates",
  "reports.range.from": "From",
  "reports.range.to": "To",
  "reports.export": "Download as CSV",
  "reports.exported": "Downloaded.",
  "reports.empty": "Nothing happened in this range",

  "reports.usage.title": "Usage",
  "reports.usage.sessions": "Lessons",
  "reports.usage.runs": "Scenes",
  "reports.usage.groups": "Groups that met",
  "reports.usage.teachers": "Teachers who taught",
  "reports.usage.minutes": "Minutes taught",
  "reports.usage.attendance": "Average attendance",
  "reports.usage.attendanceRate": "Average turnout",
  "reports.usage.online": "Online",
  "reports.usage.offline": "Offline",
  "reports.usage.seats": "Teacher seats used",
  "reports.usage.seatsOf": "{used} of {total}",

  "reports.content.title": "Which scenes worked",
  "reports.content.intro":
    "A scene a teacher comes back to is a scene that worked. That is what the repeat rate is.",
  "reports.content.column.scenario": "Scene",
  "reports.content.column.uses": "Times used",
  "reports.content.column.repeatRate": "Came back to it",
  "reports.content.column.groups": "Groups",
  "reports.content.column.teachers": "Teachers",
  "reports.content.column.minutes": "Average length",

  "reports.activity.title": "Over time",
  "reports.activity.column.month": "Month",
  "reports.activity.column.sessions": "Lessons",
  "reports.activity.column.runs": "Scenes",
  "reports.activity.column.groups": "Groups",
  "reports.activity.column.minutes": "Minutes",

  "reports.teachers.title": "Teacher activity",
  "reports.teachers.usageNotPerformance":
    "Usage, not performance. This is not a ranking and is deliberately not offered as one — how much somebody used a tool says nothing about how well they taught.",
  "reports.teachers.column.teacher": "Teacher",
  "reports.teachers.column.sessions": "Lessons",
  "reports.teachers.column.runs": "Scenes",
  "reports.teachers.column.groups": "Groups",
  "reports.teachers.column.minutes": "Minutes",

  "reports.summary.title": "In a paragraph",
  "reports.summary.generate": "Write a summary",
  "reports.summary.writing": "Writing…",
  "reports.summary.label":
    "Written by the assistant from the numbers above. It never sees a name — not a student's, not a teacher's.",
  "reports.teacherScope":
    "Your own groups. The institution-wide view belongs to the owner.",

  // ── billing ───────────────────────────────────────────────────────
  "billing.title": "Billing",
  "billing.plan.title": "Your licence",
  "billing.plan.tier": "Tier",
  "billing.plan.seats": "Teachers",
  "billing.plan.seatsOf": "{used} of {total}",
  "billing.plan.renews": "Runs until",
  "billing.plan.status": "Status",
  "billing.plan.cancelAtPeriodEnd": "Ends at the end of this period",
  "billing.tier.solo": "Solo",
  "billing.tier.small": "Small",
  "billing.tier.medium": "Medium",
  "billing.tier.large": "Large",
  "billing.tier.teachers": "Up to {count} teachers",
  "billing.tier.teachersOne": "One teacher",
  "billing.tier.current": "Your tier",
  "billing.tier.choose": "Move to {tier}",
  "billing.tier.blocked":
    "You have {used} teachers; {tier} allows {max}. Remove some first, and then you can move.",
  "billing.tier.priceElsewhere":
    "Prices are shown at checkout. They live in Stripe so that a change in price is never a change in this application.",

  "billing.portal.title": "Payment method and invoices",
  "billing.portal.body":
    "Cards, invoices and receipts are handled by Stripe. We never see a card number.",
  "billing.portal.open": "Open the billing portal",

  "billing.state.expiring.title": "Your licence runs out on {date}",
  "billing.state.expiring.body":
    "Renew before then and nothing changes. Nothing is deleted either way — your scenes, your groups and your statistics stay exactly as they are.",
  "billing.state.paymentFailed.title": "A payment did not go through",
  "billing.state.paymentFailed.body":
    "Lessons carry on. Stripe will try the card again over the next few days; you can also update it now in the billing portal.",
  "billing.state.expired.title": "Your licence has run out",
  "billing.state.expired.body":
    "New lessons cannot be started. A lesson already running is unaffected, and nothing is deleted — every scene, group, report and statistic is still yours and still exportable.",
  "billing.state.manual.title": "Billed by invoice",
  "billing.state.manual.body":
    "Your licence is set by arrangement rather than by subscription, so there is nothing to change here. Write to us and we will sort it out.",

  "billing.checkout.opening": "Opening checkout…",
  "billing.checkout.legalFirst":
    "There are documents to accept before you can pay for a licence. The data processing agreement is part of what you would be agreeing to, not an afterthought.",
  "billing.checkout.toLegal": "Read them",
  "billing.checkout.waiting":
    "Payment went through. We are waiting for Stripe to confirm it — this usually takes a few seconds, and nothing is lost if you leave this page.",
  "billing.checkout.cancelled": "Nothing was charged and nothing was changed.",

  "billing.seatLimitInline":
    "Your {tier} licence covers {max} teachers and {used} are in use. A larger tier is a minute away.",
  "billing.seatLimitAction": "See the tiers",

  "nav.support": "Support access",
  "support.title": "Support access",
  "support.intro":
    "Spontar cannot look at your institution's content on its own authority. If somebody there needs to, they ask you here — you decide, the access is read-only, and it closes by itself.",
  "support.empty": "Nobody at Spontar has asked to look at anything",
  "support.column.reason": "What they say they need it for",
  "support.column.asked": "Asked",
  "support.column.who": "Who",
  "support.column.state": "State",
  "support.column.remaining": "Left",
  "support.approve": "Let them in",
  "support.deny": "Refuse",
  "support.revoke": "Close it now",
  "support.approved": "They can look until it closes.",
  "support.denied": "Refused. They have been told.",
  "support.revoked": "Closed.",
  "support.duration.1": "one hour",
  "support.duration.8": "one working day",
  "support.duration.24": "one day",
  "support.forHours": "For {duration}",
  "support.remaining": "{count} minutes left",
  "support.readOnly":
    "Read-only, always. Nobody at Spontar can change anything in your institution, and a teacher's own notes and scores are never included.",

  "support.state.pending": "Waiting for you",
  "support.state.approved": "Open",
  "support.state.denied": "You refused",
  "support.state.revoked": "You closed it",
  "support.state.expired": "Closed",

  "support.banner.active":
    "{who} at Spontar is looking at your institution for the next {minutes} minutes, because: {reason}",
  "support.banner.readOnly": "Read-only. Nothing can be changed.",
  "support.banner.close": "Close it now",
  "support.banner.pending":
    "Somebody at Spontar has asked to look at your institution. Nothing is open until you decide.",
  "support.banner.decide": "Decide",

  "support.views.title": "What they looked at",
  "support.views.empty": "Nothing yet",
  "support.views.column.view": "Screen",
  "support.views.column.at": "When",

  "support.error.requestNotFound": "That request could not be found.",
  "support.error.alreadyDecided": "That request has already been answered.",
  "support.error.notApproved": "That access is not open.",
  "support.error.notActive": "That access has closed.",
} as const;

export type MessageKey = keyof typeof en;
